import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import Loading from "@/components/Loading";
import { trpc } from "@/trpc";
import { CommunicationCategory } from "@api/shared-runtime/communication-category";
import { CommunicationMethod } from "@api/shared-runtime/communication-method";
import { ORG_CHANNEL_META } from "./channel-meta";
import { GlobalEmailUnsubscribeBanner } from "./GlobalEmailUnsubscribeBanner";

type Props = {
  organizationBusinessUnitId: string;
  businessUnitName: string;
};

const channelMeta = ORG_CHANNEL_META;

export function OrgNotificationPreferences({
  organizationBusinessUnitId,
  businessUnitName,
}: Props) {
  const queryClient = useQueryClient();
  const queryOptions = trpc.user.listOrgNotificationPreferences.queryOptions({
    organizationBusinessUnitId,
  });
  const { data, isLoading } = useQuery(queryOptions);

  const setMutation = useMutation(
    trpc.user.setOrgNotificationPreference.mutationOptions({
      // Optimistic update so toggles feel instantaneous.
      onMutate: async (vars) => {
        await queryClient.cancelQueries(queryOptions);
        const previous = queryClient.getQueryData(queryOptions.queryKey);
        queryClient.setQueryData(queryOptions.queryKey, (old) => {
          if (!old) {
            return old;
          }
          return {
            categories: old.categories.map((c) =>
              c.categoryId !== vars.categoryId
                ? c
                : {
                    ...c,
                    channels: c.channels.map((ch) =>
                      ch.method !== vars.method
                        ? ch
                        : { ...ch, enabled: vars.enabled, isDefault: false },
                    ),
                  },
            ),
          };
        });
        return { previous };
      },
      onError: (_err, _vars, ctx) => {
        if (ctx?.previous) {
          queryClient.setQueryData(queryOptions.queryKey, ctx.previous);
        }
        toast.error("Couldn't update notification preference");
      },
      onSettled: () => queryClient.invalidateQueries(queryOptions),
    }),
  );

  if (isLoading || !data) {
    return (
      <Card>
        <CardContent className="py-8">
          <Loading />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>
          Choose how you're notified about activity in{" "}
          <span className="font-medium text-foreground">
            {businessUnitName}
          </span>
          . These settings apply only to this business unit. If you also use
          Saile as a provider, those notifications are managed separately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <GlobalEmailUnsubscribeBanner
          invalidateOnSuccess={[queryOptions.queryKey]}
        />
        {data.categories.map((category, idx) => {
          const allOff = category.channels.every((c) => !c.enabled);
          return (
            <div key={category.categoryId} className="space-y-4">
              {idx > 0 && <Separator />}

              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <h3 className="text-base font-semibold">{category.label}</h3>
                  <p className="text-sm text-muted-foreground">
                    {allOff ? category.mutedDescription : category.helperText}
                  </p>
                </div>
                <Switch
                  aria-label={`Toggle ${category.label}`}
                  checked={!allOff}
                  onCheckedChange={(value) => {
                    for (const ch of category.channels) {
                      if (ch.enabled !== value) {
                        setMutation.mutate({
                          organizationBusinessUnitId,
                          categoryId: category.categoryId,
                          method: ch.method,
                          enabled: value,
                        });
                      }
                    }
                  }}
                />
              </div>

              {!allOff && (
                <>
                  <div className="rounded-md border bg-muted/30 divide-y">
                    {category.channels.map((channel) => {
                      const meta = channelMeta[channel.method];
                      const Icon = meta.Icon;
                      return (
                        <div
                          key={channel.method}
                          className="flex items-center justify-between gap-4 px-4 py-3"
                        >
                          <div className="flex items-start gap-3">
                            <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" />
                            <div className="space-y-0.5">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm font-medium">
                                  {meta.label}
                                </Label>
                                {channel.isDefault && (
                                  <Badge variant="outline" size="small">
                                    Default
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {meta.helper}
                              </p>
                            </div>
                          </div>
                          <Switch
                            aria-label={`${meta.label} for ${category.label}`}
                            checked={channel.enabled}
                            onCheckedChange={(value) =>
                              setMutation.mutate({
                                organizationBusinessUnitId,
                                categoryId: category.categoryId,
                                method: channel.method,
                                enabled: value,
                              })
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export type { CommunicationCategory, CommunicationMethod };
